import { useState, useRef, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { Settings, Check, Bell, BellOff, Sun, Moon } from "lucide-react";
import { useCurrency } from "../../context/CurrencyContext";
import { usePageTheme } from "../../context/PageThemeContext";
import usePushNotifications from "../../hooks/usePushNotifications";
import {
  Wrap,
  TriggerBtn,
  Panel,
  SectionLabel,
  Divider,
  OptionRow,
  OptionLabel,
  CheckIcon,
  ToggleRow,
  ToggleLabel,
  ToggleIndicator,
  MobileWrap,
  MobileSectionLabel,
  MobileOptionRow,
  MobileOptionGrid,
  MobileToggleRow,
  MobileHint,
} from "./SettingsDropdown.styles";

const CURRENCIES = [
  { code: "RSD", flag: "\u{1F1F7}\u{1F1F8}", label: "RSD" },
  { code: "EUR", flag: "\u{1F1EA}\u{1F1FA}", label: "EUR" },
  { code: "USD", flag: "\u{1F1FA}\u{1F1F8}", label: "USD" },
];

const LANGUAGES = [
  { code: "EN", label: "English" },
  { code: "SR", label: "Srpski" },
];

export default function SettingsDropdown({ mobile = false }) {
  const { currency, setCurrency } = useCurrency();
  const { light, setLight } = usePageTheme();
  const { isSupported, isSubscribed, loading, subscribe, unsubscribe } =
    usePushNotifications();

  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("EN");
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleNotifications = () => {
    if (loading) return;
    isSubscribed ? unsubscribe() : subscribe();
  };

  const toggleTheme = () => setLight((p) => !p);

  /* ── Mobile: flat layout for drawer ── */
  if (mobile) {
    return (
      <MobileWrap>
        <MobileSectionLabel>Currency</MobileSectionLabel>
        <MobileOptionGrid>
          {CURRENCIES.map((c) => (
            <MobileOptionRow
              key={c.code}
              $active={currency === c.code}
              onClick={() => setCurrency(c.code)}
            >
              {c.flag} {c.label}
              {currency === c.code && <Check size={13} />}
            </MobileOptionRow>
          ))}
        </MobileOptionGrid>

        <MobileSectionLabel>Notifications</MobileSectionLabel>
        {isSupported ? (
          <MobileToggleRow onClick={toggleNotifications} disabled={loading}>
            {isSubscribed ? <Bell size={16} /> : <BellOff size={16} />}
            {isSubscribed ? "On" : "Off"}
          </MobileToggleRow>
        ) : (
          <>
            <MobileToggleRow disabled>
              <BellOff size={16} />
              Unavailable
            </MobileToggleRow>
            <MobileHint>
              Notifications aren't supported in this browser.
            </MobileHint>
          </>
        )}

        <MobileSectionLabel>Language</MobileSectionLabel>
        <MobileOptionGrid>
          {LANGUAGES.map((l) => (
            <MobileOptionRow
              key={l.code}
              $active={lang === l.code}
              onClick={() => setLang(l.code)}
              disabled={l.code === "SR"}
            >
              {l.label}
              {lang === l.code && <Check size={13} />}
            </MobileOptionRow>
          ))}
        </MobileOptionGrid>

        <MobileSectionLabel>Theme</MobileSectionLabel>
        <MobileToggleRow onClick={toggleTheme}>
          {light ? <Sun size={16} /> : <Moon size={16} />}
          {light ? "Light" : "Dark"}
        </MobileToggleRow>
      </MobileWrap>
    );
  }

  /* ── Desktop: dropdown panel ── */
  return (
    <Wrap ref={ref}>
      <TriggerBtn onClick={() => setOpen((p) => !p)} aria-label="Settings">
        <Settings size={18} />
      </TriggerBtn>

      <AnimatePresence>
        {open && (
          <Panel
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            {/* Currency */}
            <SectionLabel>Currency</SectionLabel>
            {CURRENCIES.map((c) => (
              <OptionRow
                key={c.code}
                $active={currency === c.code}
                onClick={() => setCurrency(c.code)}
              >
                <span>{c.flag}</span>
                <OptionLabel>{c.label}</OptionLabel>
                {currency === c.code && (
                  <CheckIcon>
                    <Check size={14} />
                  </CheckIcon>
                )}
              </OptionRow>
            ))}

            <Divider />

            {/* Notifications */}
            {isSupported && (
              <>
                <ToggleRow onClick={toggleNotifications} disabled={loading}>
                  {isSubscribed ? <Bell size={16} /> : <BellOff size={16} />}
                  <ToggleLabel>Notifications</ToggleLabel>
                  <ToggleIndicator $on={isSubscribed}>
                    {isSubscribed ? "ON" : "OFF"}
                  </ToggleIndicator>
                </ToggleRow>
                <Divider />
              </>
            )}

            {/* Language */}
            <SectionLabel>Language</SectionLabel>
            {LANGUAGES.map((l) => (
              <OptionRow
                key={l.code}
                $active={lang === l.code}
                onClick={() => setLang(l.code)}
                disabled={l.code === "SR"}
              >
                <OptionLabel>{l.label}</OptionLabel>
                {lang === l.code && (
                  <CheckIcon>
                    <Check size={14} />
                  </CheckIcon>
                )}
              </OptionRow>
            ))}

            <Divider />

            {/* Theme */}
            <ToggleRow onClick={toggleTheme}>
              {light ? <Sun size={16} /> : <Moon size={16} />}
              <ToggleLabel>Theme</ToggleLabel>
              <ToggleIndicator $on={false}>
                {light ? "LIGHT" : "DARK"}
              </ToggleIndicator>
            </ToggleRow>
          </Panel>
        )}
      </AnimatePresence>
    </Wrap>
  );
}
